<template>
  <div v-if="!isEmptyData || pageLoading" class="gender-report">
    <InsightHeader
      class="header"
      :loading="pageLoading"
      :reporting-message="reportingMessage"
      @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
    />
    <div class="gender-report-content">
      <GenderReportSummary
        class="summary mb-0"
        :loading="pageLoading"
        :data="data"
        :tooltips="tooltips"
        :project-name="projectName"
        :date-range="dateRange"
      />
      <GenderReportByStageWidget
        class="gender-by-stage"
        :data="data"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <GenderReportPassthroughWidget
        class="passthrough-rates"
        :data="data"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <GenderReportTable
        class="table"
        :error="error"
        :loading="pageLoading"
        :empty-data="isEmptyData"
        :data="data"
        :table-rows="tableRows"
        :table-totals="tableTotals"
        :downloading="downloading"
        @download-csv="downloadGenderTable"
      />
    </div>
    <FilterExportPreview class="mt-4" />
  </div>
  <NoDataForWidget v-else />
</template>

<script setup>
import {
  computed, onMounted, onBeforeUnmount, ref,
} from 'vue';
import { useFilters } from '@/stores/filters/filters';
import { useGenderReport } from '@/stores/insights/genderReport';
import { useRoute, useRouter } from 'vue-router';
import { useAppLoading } from '@/stores/app/loading';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useAppBase } from '@/stores/app/base';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useInsights } from '@/stores/insights/insights';
import InsightHeader from '@/components/insights/InsightHeader.vue';
import GenderReportSummary from '@/components/insights/ProcessInsights/GenderReport/GenderReportSummary.vue';
import GenderReportByStageWidget from '@/components/insights/ProcessInsights/GenderReport/GenderReportByStageWidget.vue';
import { useTooltips } from '@/stores/insights/tooltips';
import GenderReportPassthroughWidget from '@/components/insights/ProcessInsights/GenderReport/GenderReportPassthroughWidget.vue';
import GenderReportTable from '@/components/insights/ProcessInsights/GenderReport/GenderReportTable.vue';
import NoDataForWidget from '@/components/insights/NoDataForWidget.vue';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';
import { useTracking } from '@/stores/tracking/tracking';
import FilterExportPreview from '@/components/insights/ExportModal/FilterExportPreview.vue';

const emit = defineEmits(['track-page-load']);

const {
  data, isEmptyData, error, tableRows, tableTotals,
} = storeToRefs(useGenderReport());
const { pageLoading } = storeToRefs(useAppLoading());
const { authData } = storeToRefs(useAppAuthData());
const { reportingMessage } = storeToRefs(useInsights());
const { tooltips } = storeToRefs(useTooltips());
const { dateRange } = storeToRefs(useDatePicker());

const { loadFilters, setFilterConfig } = useFilters();
const { setDatePickerVariant, resetDateRange } = useDatePicker();
const { loadGenderReport, downloadGenderCSV } = useGenderReport();
const { showFilterBarSettings } = useAppBase();
const { setReportingMessage } = useInsights();
const { setExportConfig, resetExportConfig } = useInsightsExport();
const { setShareConfig, resetShareConfig } = useInsightsShare();

const route = useRoute();
const router = useRouter();

const downloading = ref(false);

const projectName = computed(() => authData.value.projectName);

const loadPage = async (params) => Promise.all([
  loadFilters(params),
  loadGenderReport(params),
]);

const downloadGenderTable = async () => {
  downloading.value = true;

  await downloadGenderCSV({
    dateRange: dateRange.value,
    projectName: projectName.value,
  });

  useTracking().insightsDownloadReport({
    reportName: route.name,
    downloadType: 'Individual Widgets',
    downloadLocation: 'Individual Widget',
    fileType: 'CSV',
    widgetName: 'Data Table (.csv)',
  });

  downloading.value = false;
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  emit('track-page-load', { page: 'Gender Reporting' });

  setFilterConfig({
    module: 'insights',
    endpoint: 'process',
    datepicker: true,
    narrow: true,
    showMyJobs: true,
  });

  setDatePickerVariant('multiOption');
  showFilterBarSettings({ route: route.name, options: [] });

  setShareConfig({
    shareable: true,
    showReportImageButton: true,
  });

  setExportConfig({
    csvExport: true,
    pngExport: true,
    pngWidgetOptions: {
      female_applied: 'Metric: % Female Applications',
      female_hired: 'Metric: % Female Hires',
      male_applied: 'Metric: % Male Applications',
      male_hired: 'Metric: % Male Hires',
      'gender-representation-by-stage': 'Graph: Gender Distribution by Stage',
      'female-vs-male-passthrough-rate': 'Graph: Gender Pass-through by Stage',
      'gender-report-table': 'Table: Gender Data',
    },
    csvOptions: [
      {
        label: 'Data Table (.csv)',
        csvDownloadFunction: () => downloadGenderCSV({
          dateRange: dateRange.value,
          projectName: projectName.value,
        }),
      },
    ],
  });

  await loadPage({ routeQuery: route.query || '', selectedMyJobs: route.query?.owned === 'true', router });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  resetDateRange();

  resetExportConfig();

  resetShareConfig();
});
</script>

<style lang="less" scoped>
.gender-report {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  padding-bottom: 16px;
}

.gender-report-content {
  width: 100%;
  display: grid;
  grid-template-areas:
    "summary summary"
    "genderByStage passthroughRates"
    "table table";
  grid-template-rows: auto 1fr auto;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.summary {
  grid-area: summary;
}
.gender-by-stage {
  grid-area: genderByStage;
}
.passthrough-rates {
  grid-area: passthroughRates;
}
.table {
  grid-area: table;
}
</style>
